import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useGateways, Gateway, isHardwareGateway } from "../context/GatewayContext";
import { getUnreadNotificationCount, getNotificationTimestamp } from '../utils/notificationUtils';
import { HomeIcon } from "@heroicons/react/24/solid";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GatewayIcon, CloudGatewayIcon } from '../components/icons/icons';
import { OAuthErrorHandler } from "../components/OAuthErrorHandler";
import WalletRewardsCard from '../components/rewards/WalletRewardsCard';
import PlusCpuIcon from '../components/icons/PlusCpuIcon';
import PageContainer from "../components/layout/PageContainer";
import { GridIcon, TableIcon } from "../components/icons/ViewIcons";
import { DashboardTable } from "../components/DashboardTable";
import { DashboardSummaryCards } from "../components/DashboardSummaryCards";
import FeedbackBanner from "../components/FeedbackBanner";
import { FC } from "react";
import { useTheme } from "../context/ThemeContext";
import DashboardEditMenu, { VisibilitySettings } from "../components/dashboard/DashboardEditMenu";

// Add custom shimmer effect class
// Add this at the top of your file
const shimmerClass = `
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.shimmer-effect.dark {
  background: linear-gradient(90deg, #232323 25%, #2a2a2a 50%, #232323 75%);
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.shimmer-effect.light {
  background: linear-gradient(90deg, #f5f5f5 25%, #fafafa 50%, #f5f5f5 75%);
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}
`;

// Add a CSS style tag to the component with our custom animation
const ShimmerStyleTag = () => (
  <style dangerouslySetInnerHTML={{ __html: shimmerClass }} />
);

const GatewayCard = ({ gateway, isDragging = false, isLoading = false }: { gateway: Gateway, isDragging?: boolean, isLoading?: boolean }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isLightMode = theme === 'light';

  const getGatewayState = (gateway: Gateway) => {
    // For cloud gateways, return unknown
    if (!isHardwareGateway(gateway)) return 'unknown';
    
    if (!gateway.state?.timestamp) return 'unknown';
    
    const now = Date.now();
    const diffMinutes = (now - gateway.state.timestamp) / 1000 / 60;
    
    if (diffMinutes < 10) return 'active';
    if (diffMinutes > 60) return 'inactive';
    return 'disabled';
  };

  const gatewayState = getGatewayState(gateway);
  const unreadCount = getUnreadNotificationCount(
    gateway.id,
    gateway.state?.status?.messages ?? [],
    getNotificationTimestamp(gateway.id)
  );

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on interactive elements
    if (e.target instanceof Element && 
        (e.target.closest('.notification-bubble') || 
         e.target.closest('.der-icon'))) {
      return;
    }
    
    if (!isDragging) {
      navigate({ to: '/gateway/$gatewayId', params: { gatewayId: gateway.id } });
    }
  };

  const GatewayIconComponent = isHardwareGateway(gateway) ? GatewayIcon : CloudGatewayIcon;

  return (
    <div 
      className="relative w-full aspect-[2/1] sm:aspect-square"
      onClick={handleCardClick}
    >
      {/* Notification bubble */}
      {!isLoading && unreadCount > 0 && (
        <Link 
          to="/notifications"
          className="notification-bubble absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold z-30 hover:opacity-80 transition-opacity"
          onClick={e => e.stopPropagation()}
        >
          {unreadCount}
        </Link>
      )}
      
      <div className={`block p-4 bg-white dark:bg-[#1D1D1D] border border-[#e0e0e0] dark:border-[#292929] rounded-[8px] shadow shadow-black/10 hover:shadow-[0_0_6px_rgba(0,184,101,0.15)] hover:border-[#00b865] dark:hover:shadow-[0_0_6px_rgba(0,255,132,0.15)] dark:hover:border-[#00FF84] transition-all duration-300 h-full w-full ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} ${isLoading ? (isLightMode ? 'shimmer-effect light' : 'shimmer-effect dark') : ''}`}>
        <div className="flex flex-col h-full justify-center items-center">
          {isLoading ? (
            <div className={`w-14 h-14 rounded-full mb-5 ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
          ) : (
            <GatewayIconComponent 
              state={gatewayState} 
              className="w-14 h-14 mb-5"
            />
          )}
          {isLoading ? (
            <div className={`w-3/4 h-5 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
          ) : (
            <h3 className="text-[#3A3A3A] dark:text-white text-base font-medium text-center truncate w-full">
              {gateway.name}
            </h3>
          )}
        </div>
      </div>
    </div>
  );
};

const OnboardGatewayCard = ({ isDragging = false }: { isDragging?: boolean }) => {
  return (
    <div className="w-full aspect-[2/1] sm:aspect-square">
      <div className={`block p-4 bg-white dark:bg-[#1D1D1D] border border-[#e0e0e0] dark:border-[#292929] rounded-[8px] shadow shadow-black/10 hover:shadow-[0_0_6px_rgba(0,184,101,0.15)] hover:border-[#00b865] dark:hover:shadow-[0_0_6px_rgba(0,255,132,0.15)] dark:hover:border-[#00FF84] transition-all duration-300 h-full w-full`}>
        <div className="flex flex-col h-full justify-center items-center">
          <PlusCpuIcon className="w-14 h-14 text-[#00b865] dark:text-[#00FF84] mb-5" />
          <h3 className="text-[#3A3A3A] dark:text-white text-base font-medium text-center">
            Add Energy Gateway
          </h3>
        </div>
      </div>
    </div>
  );
};

const OnboardCloudGatewayCard = ({ isDragging = false }: { isDragging?: boolean }) => {
  return (
    <div className="w-full aspect-[2/1] sm:aspect-square">
      <div className={`block p-4 bg-white dark:bg-[#1D1D1D] border border-[#e0e0e0] dark:border-[#292929] rounded-[8px] shadow shadow-black/10 hover:shadow-[0_0_6px_rgba(0,184,101,0.15)] hover:border-[#00b865] dark:hover:shadow-[0_0_6px_rgba(0,255,132,0.15)] dark:hover:border-[#00FF84] transition-all duration-300 h-full w-full`}>
        <div className="flex flex-col h-full justify-center items-center">
          <CloudGatewayIcon state="none" className="w-14 h-14 mb-5" />
          <h3 className="text-[#3A3A3A] dark:text-white text-base font-medium text-center">
            Add Cloud Gateway
          </h3>
        </div>
      </div>
    </div>
  );
};

const LifeBuoyIcon = ({ className = "" }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      <circle cx="12" cy="12" r="10"/>
      <path d="m4.93 4.93 4.24 4.24"/>
      <path d="m14.83 9.17 4.24-4.24"/>
      <path d="m14.83 14.83 4.24 4.24"/>
      <path d="m9.17 14.83-4.24 4.24"/>
      <circle cx="12" cy="12" r="4"/>
    </svg>
  );
};

const SupportCard = ({ isDragging = false }: { isDragging?: boolean }) => {
  return (
    <div className="w-full aspect-[2/1] sm:aspect-square">
      <div className={`block p-4 bg-white dark:bg-[#1D1D1D] border border-[#e0e0e0] dark:border-[#292929] rounded-[8px] shadow shadow-black/10 hover:shadow-[0_0_6px_rgba(0,184,101,0.15)] hover:border-[#00b865] dark:hover:shadow-[0_0_6px_rgba(0,255,132,0.15)] dark:hover:border-[#00FF84] transition-all duration-300 h-full w-full`}>
        <div className="flex flex-col h-full justify-center items-center">
          <LifeBuoyIcon className="w-14 h-14 text-[#00b865] dark:text-[#00FF84] mb-5" />
          <h3 className="text-[#3A3A3A] dark:text-white text-base font-medium text-center">
            Support
          </h3>
        </div>
      </div>
    </div>
  );
};

// Add these type definitions near the top of the file

interface CardItem {
  id: string;
  type: 'gateway' | 'onboard' | 'onboard-cloud' | 'support' | 'wallet-rewards';
  gateway?: Gateway;
}

// Add a type for the complete order
type CompleteOrder = {
  gatewayIds: string[];
  onboardPosition: number;
};

// Modify DragHandle to use the Lucide grip-vertical icon
const DragHandle = () => (
  <div 
    className="absolute bottom-4 right-4 w-8 h-8 rounded-full flex items-center justify-center z-30 text-gray-500 hover:text-gray-300 active:text-green-500 transition-colors cursor-grab"
    style={{ touchAction: 'none' }}
  >
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="20" 
      height="20" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <circle cx="9" cy="12" r="1"/>
      <circle cx="9" cy="5" r="1"/>
      <circle cx="9" cy="19" r="1"/>
      <circle cx="15" cy="12" r="1"/>
      <circle cx="15" cy="5" r="1"/>
      <circle cx="15" cy="19" r="1"/>
    </svg>
  </div>
);

// Modify SortableCard to use the drag handle
const SortableCard = ({ item }: { item: CardItem }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ 
    id: item.id
  });
  const { isLoading } = useGateways();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="relative"
    >
      {/* Card content */}
      {item.type === 'gateway' ? (
        <Link 
          to="/gateway/$gatewayId"
          params={{ gatewayId: item.gateway!.id }}
        >
          <GatewayCard gateway={item.gateway!} isDragging={isDragging} isLoading={isLoading} />
        </Link>
      ) : item.type === 'onboard' ? (
        <Link to="/onboarding-wizard">
          <OnboardGatewayCard isDragging={isDragging} />
        </Link>
      ) : item.type === 'onboard-cloud' ? (
        <Link to="/onboarding/cloud">
          <OnboardCloudGatewayCard isDragging={isDragging} />
        </Link>
      ) : item.type === 'wallet-rewards' ? (
        <WalletRewardsCard isDragging={isDragging} />
      ) : (
        <a href="https://support.sourceful.energy" target="_blank" rel="noopener noreferrer">
          <SupportCard isDragging={isDragging} />
        </a>
      )}

      {/* Drag handle */}
      <div {...attributes} {...listeners}>
        <DragHandle />
      </div>
    </div>
  );
};

// GridSkeleton component for loading state
const GridSkeleton: FC = () => {
  // Create an array to represent skeleton cards
  const skeletonCards = Array.from({ length: 8 }, (_, i) => `skeleton-card-${i}`);
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-2">
      {skeletonCards.map(cardId => (
        <div 
          key={cardId}
          className="w-full aspect-[2/1] sm:aspect-square"
        >
          <div className={`block p-4 ${isLightMode ? 'bg-white border-gray-200' : 'bg-[#1D1D1D] border-[#292929]'} border rounded-[8px] shadow shadow-black/10 h-full w-full ${isLightMode ? 'shimmer-effect light' : 'shimmer-effect dark'}`}>
            <div className="flex flex-col h-full justify-center items-center">
              <div className={`w-14 h-14 rounded-full mb-5 ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
              <div className={`w-3/4 h-5 rounded ${isLightMode ? 'bg-gray-200' : 'bg-gray-700'}`}></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const HomePage = () => {
  const { gateways, isLoading } = useGateways();
  const [orderedItems, setOrderedItems] = useState<CardItem[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>(() => {
    // Try to load saved view mode from localStorage
    try {
      const savedViewMode = localStorage.getItem('dashboardViewMode');
      if (savedViewMode === 'grid' || savedViewMode === 'table') {
        return savedViewMode;
      }
    } catch (error) {
      console.error('Error loading saved dashboard view mode:', error);
    }
    // Default to table view if nothing was saved or if there was an error
    return 'table';
  });
  
  // Add visibility settings state with localStorage loading
  const [visibilitySettings, setVisibilitySettings] = useState<VisibilitySettings>(() => {
    // Try to load saved visibility settings from localStorage
    try {
      const savedSettings = localStorage.getItem('dashboardVisibility');
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        // Validate the structure of the settings
        if (
          typeof parsedSettings === 'object' &&
          'production' in parsedSettings &&
          'consumption' in parsedSettings &&
          'batteryCharge' in parsedSettings &&
          'rewards' in parsedSettings
        ) {
          return parsedSettings as VisibilitySettings;
        }
      }
    } catch (error) {
      console.error('Error loading saved dashboard visibility settings:', error);
    }
    // Default settings if nothing was saved or if there was an error
    return {
      production: true,
      consumption: true,
      batteryCharge: true,
      rewards: false
    };
  });
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Save visibility settings to localStorage when they change
  const handleVisibilityChange = (newSettings: VisibilitySettings) => {
    setVisibilitySettings(newSettings);
    localStorage.setItem('dashboardVisibility', JSON.stringify(newSettings));
  };

  // Function to handle view mode change and save to localStorage
  const handleViewModeChange = (mode: 'grid' | 'table') => {
    setViewMode(mode);
    localStorage.setItem('dashboardViewMode', mode);
  };

  useEffect(() => {
    if (!isLoading) {
      try {
        const savedOrder = localStorage.getItem('gatewayOrder');
        if (savedOrder) {
          const orderData = JSON.parse(savedOrder);
          
          // Validate the parsed data has the expected structure
          if (orderData && 
              Array.isArray(orderData.gatewayIds) && 
              typeof orderData.onboardPosition === 'number') {
            
            const ordered = orderData.gatewayIds
              .map((id: string): CardItem | undefined => {
                const gateway = gateways.find(g => g.id === id);
                if (gateway) {
                  return { id: gateway.id, type: 'gateway', gateway };
                }
                return undefined;
              })
              .filter((item: CardItem | undefined): item is CardItem => item !== undefined);
            
            // Add any new gateways that weren't in the saved order
            const newGateways = gateways
              .filter(g => !orderData.gatewayIds.includes(g.id))
              .map(g => ({ id: g.id, type: 'gateway' as const, gateway: g }));
            
            // Create complete list with onboard and support cards at the saved position
            const allItems = [...ordered, ...newGateways];
            const onboardCard = { id: 'onboard', type: 'onboard' as const };
            const onboardCloudCard = { id: 'onboard-cloud', type: 'onboard-cloud' as const };
            const supportCard = { id: 'support', type: 'support' as const };
            
            // Ensure onboardPosition is within valid bounds
            const validPosition = Math.min(
              Math.max(0, orderData.onboardPosition), 
              allItems.length
            );
            
            // Insert onboard, cloud onboard, and support cards at the saved position
            allItems.splice(validPosition, 0, onboardCard, onboardCloudCard, supportCard);
            
            setOrderedItems(allItems);
          } else {
            // Invalid saved data, fall back to default order
            setOrderedItems([
              { id: 'wallet-rewards', type: 'wallet-rewards' },
              ...gateways.map(g => ({ id: g.id, type: 'gateway' as const, gateway: g })),
              { id: 'onboard', type: 'onboard' },
              { id: 'onboard-cloud', type: 'onboard-cloud' },
              { id: 'support', type: 'support' }
            ]);
          }
        } else {
          // No saved order, use default with wallet-rewards card
          setOrderedItems([
            { id: 'wallet-rewards', type: 'wallet-rewards' },
            ...gateways.map(g => ({ id: g.id, type: 'gateway' as const, gateway: g })),
            { id: 'onboard', type: 'onboard' },
            { id: 'onboard-cloud', type: 'onboard-cloud' },
            { id: 'support', type: 'support' }
          ]);
        }
      } catch (error) {
        // If there's any error parsing the saved order, fall back to default
        console.error('Error loading saved gateway order:', error);
        setOrderedItems([
          { id: 'wallet-rewards', type: 'wallet-rewards' },
          ...gateways.map(g => ({ id: g.id, type: 'gateway' as const, gateway: g })),
          { id: 'onboard', type: 'onboard' },
          { id: 'onboard-cloud', type: 'onboard-cloud' },
          { id: 'support', type: 'support' }
        ]);
      }
    }
  }, [gateways, isLoading]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOrderedItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        const newOrder = arrayMove(items, oldIndex, newIndex);
        
        // Save the complete order to localStorage
        const onboardPosition = newOrder.findIndex(item => item.type === 'onboard');
        const orderData: CompleteOrder = {
          gatewayIds: newOrder
            .filter(item => item.type === 'gateway')
            .map(item => item.id),
          onboardPosition
        };
        
        localStorage.setItem('gatewayOrder', JSON.stringify(orderData));
        
        return newOrder;
      });
    }
  };
  
  return (
    <>
      <OAuthErrorHandler />
      <ShimmerStyleTag />
      <PageContainer>
        {/* Deprecation Notice */}
        <div className="mb-4 sm:mb-6 mt-1 sm:mt-1">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-500 p-4 rounded-r-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400 dark:text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <span className="font-semibold">This dashboard is deprecated and should not be used.</span> Please use the{" "}
                  <a 
                    href="https://app.sourceful.energy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline font-medium hover:text-yellow-900 dark:hover:text-yellow-100"
                  >
                    Sourceful App
                  </a>{" "}
                  instead.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6 sm:mb-8 mt-1 sm:mt-1">
          <div className="flex items-center justify-between gap-2 mb-2 sm:mb-4">
            <div className="flex items-center gap-2">
              <HomeIcon className="w-5 h-5 text-[#00FF84]" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                Your Dashboard
              </h1>
            </div>
            
            {/* View Toggle with edit button */}
            <div className="flex items-center gap-2">
              <div className="flex items-center rounded-lg bg-gray-100 dark:bg-[#1D1D1D] border border-gray-300 dark:border-[#292929] p-1">
                <button
                  onClick={() => handleViewModeChange('table')}
                  className={`p-1.5 rounded-md ${viewMode === 'table' ? 'bg-gray-200 dark:bg-[#292929]' : ''}`}
                  aria-label="Table View"
                >
                  <TableIcon className="w-5 h-5" active={viewMode === 'table'} />
                </button>
                <button
                  onClick={() => handleViewModeChange('grid')}
                  className={`p-1.5 rounded-md ${viewMode === 'grid' ? 'bg-gray-200 dark:bg-[#292929]' : ''}`}
                  aria-label="Grid View"
                >
                  <GridIcon className="w-5 h-5" active={viewMode === 'grid'} />
                </button>
              </div>
              {/* Edit Menu */}
              <DashboardEditMenu 
                visibilitySettings={visibilitySettings}
                onVisibilityChange={handleVisibilityChange}
              />
            </div>
          </div>
        </div>

        {/* Dashboard Summary Cards - Only render in table view */}
        {viewMode === 'table' && <DashboardSummaryCards gateways={gateways} visibilitySettings={visibilitySettings} />}
        
        {/* Grid or Table View based on viewMode */}
        {isLoading ? (
          <div className="transition-all duration-500 rounded-[8px] overflow-hidden opacity-100">
            {viewMode === 'grid' ? (
              <GridSkeleton />
            ) : (
              <>
                <div className="bg-[#1D1D1D] border border-[#292929] rounded-[8px]">
                  <DashboardTable gateways={gateways} visibilitySettings={visibilitySettings} />
                </div>
                <FeedbackBanner />
              </>
            )}
          </div>
        ) : (
          <div className="transition-opacity duration-500 opacity-100">
            {viewMode === 'grid' ? (
              <div className="transition-all duration-500">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={orderedItems.map(item => item.id)}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-2">
                      {orderedItems.map((item) => (
                        <SortableCard
                          key={item.id}
                          item={item}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            ) : (
              <div className="transition-all duration-500 rounded-[8px]">
                <DashboardTable gateways={gateways} visibilitySettings={visibilitySettings} />
                <FeedbackBanner />
              </div>
            )}
          </div>
        )}
      </PageContainer>
    </>
  );
};

export default HomePage; 